import React, { ReactNode, useEffect, useRef, useState, KeyboardEvent } from 'react';

import { IoIosArrowDown } from 'react-icons/io';

interface PriceOriginComputed {
	basedOnPriceType: string;
	calculationType: string;
}

interface PriceOrigin {
	type: string;
	computed: PriceOriginComputed | null;
}

interface Price {
	value: string;
	currency: string;
	roundedHumanFormat(): ReactNode;
}

interface ProductPrice {
	origin: PriceOrigin | null;
	price: Price | null;
	priority: number;
	type: string;
}

interface ProductComputedPrice {
	type: string;
	price: Price;
	description: string;
	selected: boolean;
}

interface ReferencePrice {
	calculatedPrice: ProductComputedPrice;
	sourcePrice: ProductPrice;
}

interface PriceSelectProps {
	referencePrice: ReferencePrice;
	referencePrices: ReferencePrice[];
	onSelect: (value: string, reactNode: ReactNode) => void;
	dataTestId?: string;
}

function renderReferencePricePrompt(price: ReferencePrice) {
	return (
		<>
			<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
			<strong className='block leading-normal'>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
		</>
	);
}

export function PriceSelect({ referencePrice, referencePrices, onSelect, dataTestId }: PriceSelectProps) {
	const [cursor, setCursor] = useState(-1);
	const [openSelect, setOpenSelect] = useState(false);
	const ref = useRef<HTMLDivElement | null>(null);

	const handleValueSelect = (value: string, reactNode: ReactNode) => {
		setOpenSelect(false);
		onSelect(value, reactNode);
	};

	const handleOpenCloseSelect = () => {
		setOpenSelect(!openSelect);
	};

	const moveCursorDown = () => {
		const nextOption = cursor + 1;
		if (nextOption >= referencePrices.length) {
			return;
		}
		setCursor(nextOption);
	};

	const moveCursorUp = () => {
		const nextOption = cursor - 1;
		if (nextOption < 0) {
			return;
		}
		setCursor(nextOption);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			setOpenSelect(false);
			return;
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();

			moveCursorUp();
			return;
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault();

			moveCursorDown();
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();

			handleValueSelect(
				referencePrices?.[cursor].calculatedPrice.type,
				renderReferencePricePrompt(referencePrices?.[cursor])
			);
		}
	};

	useEffect(() => {
		const selectedIndex = referencePrices.findIndex((item) => item.calculatedPrice && item.calculatedPrice.selected);
		setCursor(selectedIndex);

		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) {
				setOpenSelect(false);
			}
		};

		if (openSelect) {
			document.addEventListener('mousedown', handleClickOutside);
			return;
		}
		document.removeEventListener('mousedown', handleClickOutside);
	}, [openSelect]);

	return (
		<div
			className='relative w-full h-full text-right cursor-pointer bg-white group focus:outline-none'
			data-testid={dataTestId || 'segmented-select-prompt'}
			onKeyDown={handleKeyDown}
			tabIndex={-1}
			ref={ref}
		>
			<div
				className={`relative h-full flex flex-col items-end justify-center pr-5 text-right ${
					openSelect ? 'bg-white' : ''
				}`}
				title={referencePrice.calculatedPrice.description}
				onClick={handleOpenCloseSelect}
			>
				{renderReferencePricePrompt(referencePrice)}
				<IoIosArrowDown
					className={`absolute right-0.5 top-1/2 -mt-2 w-4 h-4 text-gray-400 group-hover:text-black transition ${
						openSelect ? '!text-black rotate-180' : ''
					}`}
				/>
			</div>
			{openSelect && (
				<div className='absolute right-0 -mr-px bottom-full rounded-t-md border-l border-r border-t border-dashed border-gray-200 bg-white overflow-hidden'>
					<ul className='list-none divide-y divide-dashed divide-gray-200 text-black'>
						{referencePrices.map((price, i) => {
							return (
								<li
									key={price.calculatedPrice.type}
									value={price.calculatedPrice.type}
									data-testid='segmented-select-option'
									className={`px-5 py-2 leading-normal transition hover:bg-cBlue-50 cursor-pointer ${
										cursor === i ? 'text-white !bg-cBlue-400 !cursor-default' : ''
									}`}
									onClick={() => handleValueSelect(price.calculatedPrice.type, renderReferencePricePrompt(price))}
								>
									<div className='text-[12px] whitespace-nowrap'>{price.calculatedPrice.description}</div>
									{renderReferencePricePrompt(price)}
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</div>
	);
}
